import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import ServiceCard from "../components/ServiceCard";
import { localBusinessSchema } from "../components/seoSchemas";
import { projects, serviceGroups, site } from "../data/site";

export default function HomePage() {
  return (
    <>
      <SEO
        title="Polishing Singapore | Stone Polishing, Restoration & Cleaning"
        description="Stone polishing, restoration and cleaning in Singapore for marble, granite, quartz, terrazzo, tile, parquet, concrete and natural stone surfaces."
        canonical={`${site.domain}/`}
        jsonLd={localBusinessSchema()}
      />

      <section className="relative min-h-[92vh] overflow-hidden bg-foreground-950 text-white">
        <img src={site.heroImages[0]} alt="Stone floor polishing machine" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/82 to-foreground-950/20" />
        <div className="section-shell relative flex min-h-[92vh] items-center pt-24">
          <div className="max-w-4xl soft-reveal">
            <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-bold text-white backdrop-blur">
              <Sparkles size={16} />
              Stone, tile, parquet and concrete surface care
            </p>
            <h1 className="mt-6 font-heading text-5xl font-bold leading-[0.98] md:text-7xl lg:text-8xl">{site.heroTitle}</h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-background-100 md:text-xl">{site.heroLead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="btn-primary">
                Request a Surface Assessment
                <ArrowRight size={18} />
              </Link>
              <Link to="/services" className="btn-secondary border-white/30 bg-white/10 text-white hover:bg-white hover:text-foreground-950">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-white">
        <div className="section-shell grid divide-y divide-background-200 md:grid-cols-3 md:divide-x md:divide-y-0">
          {[
            ["Surface types", "Marble, granite, quartz, terrazzo, tile, parquet, concrete and natural stone."],
            ["Work settings", "Homes, offices, restaurants, hotels, retail units and industrial floors."],
            ["Scope control", "Inspection-first polishing, cleaning, repair, restoration and maintenance advice."],
          ].map(([title, copy]) => (
            <div key={title} className="py-8 md:px-8">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{title}</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad bg-background-50">
        <div className="section-shell">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.3fr] lg:items-end">
            <div>
              <p className="eyebrow">Main service categories</p>
              <h2 className="mt-3 font-heading text-4xl font-bold leading-tight md:text-5xl">One surface care website, organized by material.</h2>
              <p className="mt-5 text-base leading-8 text-foreground-700">
                Each surface type needs a different care method, so the site keeps stone, tile, parquet and concrete services clear instead of mixing every polishing job into one generic page.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {serviceGroups.slice(0, 8).map((group) => (
                <Link key={group.slug} to={`/services#${group.slug}`} className="surface-card group p-5 transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 h-12 w-12 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center">
                    <CheckCircle2 size={22} />
                  </div>
                  <h3 className="font-heading text-2xl font-bold">{group.name}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-700">{group.intro}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="section-shell">
          <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div>
              <p className="eyebrow">Featured pages</p>
              <h2 className="mt-3 font-heading text-4xl font-bold md:text-5xl">High-intent polishing services</h2>
            </div>
            <Link to="/services" className="btn-secondary">
              All service pages
              <ArrowRight size={17} />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {site.featuredServices.map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-foreground-950 text-white">
        <div className="section-shell grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <p className="eyebrow text-primary-200">Project surface examples</p>
            <h2 className="mt-3 font-heading text-4xl font-bold leading-tight md:text-5xl">Project images organized by actual surface type.</h2>
            <p className="mt-5 text-base leading-8 text-background-200">
              Stone, restaurant floor, concrete, hotel floor and tile cleaning visuals help show the kinds of surfaces covered across the service pages.
            </p>
            <Link to="/portfolio" className="mt-7 btn-primary">
              View project visuals
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {projects.slice(0, 4).map((project) => (
              <div key={project.title} className="overflow-hidden rounded-md border border-white/10 bg-white/5">
                <img src={project.images[0]} alt={project.title} className="h-52 w-full object-cover" />
                <div className="p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-200">{project.tag}</p>
                  <h3 className="mt-2 font-heading text-xl font-bold">{project.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-background-50">
        <div className="section-shell">
          <div className="surface-card grid overflow-hidden lg:grid-cols-2">
            <img src="/assets/polishing/about_surface_samples.webp" alt="Stone surface samples" className="h-full min-h-[360px] w-full object-cover" />
            <div className="p-8 md:p-12">
              <p className="eyebrow">About the service</p>
              <h2 className="mt-3 font-heading text-4xl font-bold">Stone specialists for routine care and restoration work.</h2>
              <p className="mt-5 text-base leading-8 text-foreground-700">
                {site.aboutParagraphs[0]}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/about" className="btn-secondary">Read About Us</Link>
                <Link to="/contact" className="btn-primary">Contact Polishing Singapore</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
